import type { Dispatch, SetStateAction } from 'react'
import SaveRoundedIcon from '@mui/icons-material/SaveRounded'
import SettingsBackupRestoreRoundedIcon from '@mui/icons-material/SettingsBackupRestoreRounded'
import AutoFixHighRoundedIcon from '@mui/icons-material/AutoFixHighRounded'
import UndoRoundedIcon from '@mui/icons-material/UndoRounded'
import MoreHorizRoundedIcon from '@mui/icons-material/MoreHorizRounded'
import ContentCutRoundedIcon from '@mui/icons-material/ContentCutRounded'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded'
import { Button } from '../Common/Button'
import { IconButton } from '../Common/IconButton'
import { Pressable } from '../Common/Pressable'
import { Tip } from '../Common/Hover'
import type { ChecklistItem as BoardChecklistItem } from '../../types/schema'
import type { useRecurringLens } from '../../context/recurringLensContextCore'
import { BOARD_SCALE_MIN, BOARD_SCALE_MAX } from './useBoardViewport'

export interface BoardSessionToolsProps {
  canEditCanvas: boolean
  canCreateChecklist: boolean
  addGateAt: (gateType: 'GATE_AND' | 'GATE_OR', position: { x: number; y: number }) => Promise<unknown>
  getNextFreePosition: () => { x: number; y: number }
  isGestureKnifeActive: boolean
  setIsGestureKnifeActive: (active: boolean) => void
  boardScale: number
  adjustBoardScale: (direction: 1 | -1) => void
  isBoardDirty: boolean
  saveBoard: () => Promise<void>
  canUndoBoard: boolean
  undoBoard: () => void
  restoreBoard: () => void
  boardScopedNodes: BoardChecklistItem[]
  spreadCanvasNodes: () => void
  isBoardToolMenuOpen: boolean
  setIsBoardToolMenuOpen: Dispatch<SetStateAction<boolean>>
  canAddChecklistHere: boolean
  startNewDraftChecklist: () => void
  recurringLens: ReturnType<typeof useRecurringLens>
  onImportChecklist?: () => void
  recurringParentEligibility: { ok: boolean; reason?: string }
  isMiniMapVisible: boolean
  setIsMiniMapVisible: Dispatch<SetStateAction<boolean>>
  canEditStructure: boolean
  setIsCalendarShelfOpen: (open: boolean) => void
}

export function BoardSessionTools({
  canEditCanvas,
  canCreateChecklist,
  addGateAt,
  getNextFreePosition,
  isGestureKnifeActive,
  setIsGestureKnifeActive,
  boardScale,
  adjustBoardScale,
  isBoardDirty,
  saveBoard,
  canUndoBoard,
  undoBoard,
  restoreBoard,
  boardScopedNodes,
  spreadCanvasNodes,
  isBoardToolMenuOpen,
  setIsBoardToolMenuOpen,
  canAddChecklistHere,
  startNewDraftChecklist,
  recurringLens,
  onImportChecklist,
  recurringParentEligibility,
  isMiniMapVisible,
  setIsMiniMapVisible,
  canEditStructure,
  setIsCalendarShelfOpen,
}: BoardSessionToolsProps) {
  {/*
   * Session toolbar (right-aligned, beneath the close ×). For editors:
   * Save · AND-gate · OR-gate · Undo · Restore · Options(…).
   * For viewers: zoom controls + Options. Material-UI icons throughout.
   */}
  return (
    <div className="checklistBoardSessionTools">
      {canEditCanvas && (
        <>
          {canCreateChecklist && (
            <>
              <Tip label="Add AND gate" group="checklistToolbar">
                <Button
                  variant="tertiary"
                  size="sm"
                  className="isGateAnd"
                  aria-label="Add AND gate"
                  onClick={() => void addGateAt('GATE_AND', getNextFreePosition())}
                >
                  <span className="checklistBoardGateGlyph">&</span>
                </Button>
              </Tip>
              <Tip label="Add OR gate" group="checklistToolbar">
                <Button
                  variant="tertiary"
                  size="sm"
                  className="isGateOr"
                  aria-label="Add OR gate"
                  onClick={() => void addGateAt('GATE_OR', getNextFreePosition())}
                >
                  <span className="checklistBoardGateGlyph">||</span>
                </Button>
              </Tip>
            </>
          )}
          <IconButton
            variant="tertiary"
            size="sm"
            tipGroup="checklistToolbar"
            pressed={isGestureKnifeActive}
            label={isGestureKnifeActive ? 'Disable wire knife' : 'Enable wire knife'}
            icon={<ContentCutRoundedIcon fontSize="inherit" />}
            onClick={() => setIsGestureKnifeActive(!isGestureKnifeActive)}
          />
          <div className="checklistBoardZoomDual" role="group" aria-label="Canvas zoom">
            <Tip label={`Zoom in (${Math.round(boardScale * 100)}%)`} group="checklistToolbar">
              <Pressable
                className="checklistBoardZoomHalf"
                aria-label="Zoom in"
                disabled={boardScale >= BOARD_SCALE_MAX - 1e-6}
                onClick={() => adjustBoardScale(1)}
              >
                <AddRoundedIcon fontSize="inherit" />
              </Pressable>
            </Tip>
            <span className="checklistBoardZoomDivider" aria-hidden="true" />
            <Tip label={`Zoom out (${Math.round(boardScale * 100)}%)`} group="checklistToolbar">
              <Pressable
                className="checklistBoardZoomHalf"
                aria-label="Zoom out"
                disabled={boardScale <= BOARD_SCALE_MIN + 1e-6}
                onClick={() => adjustBoardScale(-1)}
              >
                <RemoveRoundedIcon fontSize="inherit" />
              </Pressable>
            </Tip>
          </div>
          <IconButton
            variant={isBoardDirty ? 'secondary' : 'tertiary'}
            size="sm"
            tipGroup="checklistToolbar"
            label="Save positions & connections"
            disabled={!isBoardDirty}
            icon={<SaveRoundedIcon fontSize="inherit" />}
            onClick={() => void saveBoard()}
          />
          <IconButton
            variant="tertiary"
            size="sm"
            tipGroup="checklistToolbar"
            label="Undo last change"
            disabled={!canUndoBoard}
            icon={<UndoRoundedIcon fontSize="inherit" />}
            onClick={undoBoard}
          />
          <IconButton
            variant="tertiary"
            size="sm"
            tipGroup="checklistToolbar"
            label="Restore to last saved"
            disabled={!isBoardDirty}
            icon={<SettingsBackupRestoreRoundedIcon fontSize="inherit" />}
            onClick={restoreBoard}
          />
          <IconButton
            variant="tertiary"
            size="sm"
            tipGroup="checklistToolbar"
            label="Spread out overlapping nodes"
            disabled={boardScopedNodes.length === 0}
            icon={<AutoFixHighRoundedIcon fontSize="inherit" />}
            onClick={spreadCanvasNodes}
          />
        </>
      )}
      {/* Viewer-visible copy of the zoom dual (editors see one between
       * CUT and SAVE, viewers see this one before the tools menu. Same handlers
       * — `boardScale` is shared state, not duplicated. */}
      {!canEditCanvas && (
        <div className="checklistBoardZoomDual" role="group" aria-label="Canvas zoom">
          <Tip label={`Zoom in (${Math.round(boardScale * 100)}%)`} group="checklistToolbar">
            <Pressable
              className="checklistBoardZoomHalf"
              aria-label="Zoom in"
              disabled={boardScale >= BOARD_SCALE_MAX - 1e-6}
              onClick={() => adjustBoardScale(1)}
            >
              <AddRoundedIcon fontSize="inherit" />
            </Pressable>
          </Tip>
          <span className="checklistBoardZoomDivider" aria-hidden="true" />
          <Tip label={`Zoom out (${Math.round(boardScale * 100)}%)`} group="checklistToolbar">
            <Pressable
              className="checklistBoardZoomHalf"
              aria-label="Zoom out"
              disabled={boardScale <= BOARD_SCALE_MIN + 1e-6}
              onClick={() => adjustBoardScale(-1)}
            >
              <RemoveRoundedIcon fontSize="inherit" />
            </Pressable>
          </Tip>
        </div>
      )}
      <div className="checklistBoardToolMenuWrap">
        <IconButton
          variant="tertiary"
          size="sm"
          tipGroup="checklistToolbar"
          label="Open checklist canvas tools"
          aria-expanded={isBoardToolMenuOpen}
          icon={<MoreHorizRoundedIcon fontSize="inherit" />}
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.preventDefault()
            event.stopPropagation()
            setIsBoardToolMenuOpen((current) => !current)
          }}
        />
        {isBoardToolMenuOpen && (
          <div className="checklistBoardToolMenu" onPointerDown={(event) => event.stopPropagation()}>
            {canAddChecklistHere && (
              <Pressable
                onClick={() => {
                  // Popup-first create: always open the draft drawer here,
                  // regardless of recurringLens vs main canvas. The drawer
                  // materializes the item on Save (or drops it on Discard).
                  startNewDraftChecklist()
                  setIsBoardToolMenuOpen(false)
                }}
              >
                Add new checklist
              </Pressable>
            )}
            {/* "Add from template" only makes sense on the INDIVIDUAL
             *  canvas — the recurring lens intentionally has no template
             *  library. Also gated by canAddChecklistHere so recurring
             *  at board-level hides both entries. */}
            {!recurringLens && canCreateChecklist && onImportChecklist && (
              <Pressable
                onClick={() => {
                  onImportChecklist()
                  setIsBoardToolMenuOpen(false)
                }}
              >
                Add from template
              </Pressable>
            )}
            {/* Recurring at board / ineligible parent — surface why
             *  the add action is missing so the user knows what to do. */}
            {recurringLens && !recurringParentEligibility.ok && (
              <div className="checklistBoardToolMenuHint" role="note">
                {recurringParentEligibility.reason}
              </div>
            )}
            <Pressable
              onClick={() => {
                setIsMiniMapVisible((current) => !current)
                setIsBoardToolMenuOpen(false)
              }}
            >
              {isMiniMapVisible ? 'Close Map view' : 'Open Map view'}
            </Pressable>
            {canEditStructure && (
              <Pressable
                onClick={() => {
                  setIsCalendarShelfOpen(true)
                  setIsBoardToolMenuOpen(false)
                }}
              >
                Open visionboard
              </Pressable>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
